import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import ClientRestrictedFeatureOverlay from './ClientRestrictedFeatureOverlay';
import styles from './clientRestricted.module.scss';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  getSpendSupplier,
  gatTotalOrdersBymonth,
  getCurrentMonthTopSuppliers,
  getCurrentMonthTopProducts,
} from '../../../../api/chart';

/* ── Paleta verde Cado ──────────────────────────────────── */
const G = ['#2D6A4F','#40916C','#49A35B','#377F56','#6BBF7B','#74C69D','#1B4332','#95D5A0','#B7E4C7'];

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

/* ── Helpers ────────────────────────────────────────────── */
const toNum = (v) => {
  if (v == null) return 0;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
};

const fmtUSD = (v) =>
  new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(toNum(v));

const fmtAxis = (v) => {
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${Number(v).toFixed(0)}`;
};

const nowMonthStr = () => {
  const d = new Date();
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/* ── Icons (Lucide-style SVG paths) ────────────────────── */
const ICON_PATHS = {
  dollar:   ['M12 1v22', 'M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6'],
  bag:      ['M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z', 'M3 6h18', 'M16 10a4 4 0 01-8 0'],
  truck:    ['M1 3h15v13H1z', 'M16 8h4l3 3v5h-7V8z', 'M5.5 21a1.5 1.5 0 100-3 1.5 1.5 0 000 3z', 'M18.5 21a1.5 1.5 0 100-3 1.5 1.5 0 000 3z'],
  barChart: ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
  trophy:   ['M8.21 13.89L7 23l5-3 5 3-1.21-9.12', 'M15 7h3a2 2 0 012 2v1a2 2 0 01-2 2h-3', 'M9 7H6a2 2 0 00-2 2v1a2 2 0 002 2h3', 'M12 17v-4', 'M12 7V3'],
  package:  ['M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 001 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z', 'M3.27 6.96L12 12.01l8.73-5.05', 'M12 22.08V12'],
};

const Icon = ({ name, size = 18, color = 'currentColor', sw = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    {(ICON_PATHS[name] || []).map((d, i) => <path key={i} d={d} />)}
  </svg>
);

/* ── Shimmer skeleton ───────────────────────────────────── */
const Sk = ({ h = 20, r = 8 }) => (
  <div style={{
    height: h, borderRadius: r,
    background: 'linear-gradient(90deg,#f3f3f3 25%,#e8e8e8 50%,#f3f3f3 75%)',
    backgroundSize: '300% 100%', animation: 'at-sk 1.5s ease infinite',
  }} />
);

/* ── Dark tooltip ───────────────────────────────────────── */
const DarkTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0d0d0d', color: '#fff', padding: '10px 16px',
      borderRadius: 12, fontSize: 13, boxShadow: '0 10px 32px rgba(0,0,0,.4)',
      border: '1px solid #1a1a1a',
    }}>
      <div style={{ fontWeight: 700, color: '#74C69D', marginBottom: 4 }}>
        {payload[0]?.payload?.name}
      </div>
      <div>{fmtUSD(payload[0]?.value)}</div>
    </div>
  );
};

/* ── Y-axis tick truncado ───────────────────────────────── */
const YTick = ({ x, y, payload }) => {
  const s = String(payload.value ?? '');
  const label = s.length > 16 ? s.slice(0, 15) + '…' : s;
  return (
    <text x={x} y={y} dy={4} textAnchor="end" fill="#999" fontSize={11}>
      {label}
    </text>
  );
};

/* ── Bar chart horizontal ───────────────────────────────── */
const HBar = ({ data, loading }) => {
  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px 0' }}>
        {[80, 65, 50, 42].map((w, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 80, height: 11, borderRadius: 4, background: '#f3f3f3' }} />
            <div style={{ flex: 1, height: 20, borderRadius: 6, background: `linear-gradient(90deg,#e8e8e8 ${w}%,#f5f5f5 ${w}%)` }} />
          </div>
        ))}
      </div>
    );
  }
  if (!data?.length) {
    return (
      <div style={{ textAlign: 'center', color: '#aaa', padding: '44px 0', fontSize: 13 }}>
        Sin movimientos este mes
      </div>
    );
  }
  const h = Math.max(220, data.length * 50);
  return (
    <div style={{ width: '100%', height: h, minWidth: 0 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 24, left: 115, bottom: 4 }}
          barCategoryGap="28%"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f5f5f5" />
          <XAxis
            type="number"
            tick={{ fontSize: 10, fill: '#aaa' }}
            tickFormatter={fmtAxis}
            axisLine={false} tickLine={false}
          />
          <YAxis
            type="category" dataKey="name"
            tick={<YTick />}
            axisLine={false} tickLine={false}
            width={110}
          />
          <Tooltip content={<DarkTip />} cursor={{ fill: 'rgba(45,106,79,.05)', radius: 6 }} />
          <Bar dataKey="value" radius={[0, 10, 10, 0]} maxBarSize={26}>
            {data.map((_, i) => <Cell key={i} fill={G[i % G.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── KPI Card ───────────────────────────────────────────── */
const KpiCard = ({ label, value, sub, accent, icon, loading }) => (
  <div style={{
    background: '#fff', borderRadius: 18,
    padding: '22px 24px',
    boxShadow: '0 2px 24px rgba(0,0,0,.07)',
    borderLeft: `4px solid ${accent}`,
    display: 'flex', flexDirection: 'column', gap: 10,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#888', textTransform: 'uppercase', letterSpacing: 1.5 }}>
        {label}
      </div>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} size={17} color={accent} />
      </div>
    </div>
    {loading
      ? <Sk h={36} r={6} />
      : <div style={{ fontSize: 30, fontWeight: 900, color: '#111', letterSpacing: -1.5, lineHeight: 1 }}>
          {value ?? '—'}
        </div>
    }
    {sub && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{sub}</div>}
  </div>
);

/* ── Card wrapper ───────────────────────────────────────── */
const Card = ({ children, style }) => (
  <div style={{
    background: '#fff', borderRadius: 18,
    padding: '24px 24px 28px',
    boxShadow: '0 2px 20px rgba(0,0,0,.06)',
    ...style,
  }}>
    {children}
  </div>
);

/* ── Section Header ─────────────────────────────────────── */
const SectionHeader = ({ icon, title, badge, sub, style }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, ...style }}>
    {icon && (
      <div style={{
        width: 34, height: 34, borderRadius: 10, background: '#f0faf4',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon name={icon} size={16} color="#2D6A4F" />
      </div>
    )}
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ fontSize: 17, fontWeight: 900, margin: 0, color: '#111' }}>{title}</h3>
        {badge && (
          <span style={{
            fontSize: 10, fontWeight: 900, letterSpacing: 2,
            background: 'linear-gradient(135deg,#1B4332,#49A35B)',
            color: '#fff', padding: '3px 10px', borderRadius: 99,
          }}>
            {badge}
          </span>
        )}
      </div>
      {sub && <div style={{ fontSize: 12, color: '#777', marginTop: 3 }}>{sub}</div>}
    </div>
  </div>
);

/* ── Ranking Row ────────────────────────────────────────── */
const RankRow = ({ rank, name, amount, sub, max, color }) => {
  const pct = max > 0 ? Math.min((toNum(amount) / max) * 100, 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <div style={{
        minWidth: 28, height: 28, borderRadius: '50%', background: color,
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 900, flexShrink: 0, marginTop: 2,
      }}>
        {rank}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
          <div>
            <div style={{
              fontSize: 13, fontWeight: 700, color: '#111',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 170,
            }}>
              {name}
            </div>
            {sub && <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>{sub}</div>}
          </div>
          <div style={{ fontSize: 13, color: '#2D6A4F', fontWeight: 800, flexShrink: 0 }}>
            {fmtUSD(amount)}
          </div>
        </div>
        <div style={{ background: '#f0f0f0', borderRadius: 99, height: 5, overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`, height: '100%', background: color, borderRadius: 99,
            transition: 'width .9s cubic-bezier(.4,0,.2,1)',
          }} />
        </div>
      </div>
    </div>
  );
};

/* ── Gold Gate ──────────────────────────────────────────── */
const GoldGate = () => (
  <div style={{
    position: 'absolute', inset: 0, borderRadius: 18,
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
  }}>
    <div style={{
      background: '#fff', borderRadius: 22, padding: '32px 40px', textAlign: 'center',
      boxShadow: '0 20px 60px rgba(0,0,0,.15)', maxWidth: 320,
    }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        background: 'linear-gradient(135deg,#1B4332,#49A35B)',
        color: '#fff', fontSize: 10, fontWeight: 900, letterSpacing: 3,
        padding: '5px 16px', borderRadius: 99, marginBottom: 18,
      }}>
        <Icon name="trophy" size={12} color="#fff" />
        GOLD / ENTERPRISE
      </div>
      <div style={{ fontSize: 19, fontWeight: 900, color: '#111', marginBottom: 10 }}>
        Analytics avanzados
      </div>
      <div style={{ fontSize: 13, color: '#888', lineHeight: 1.75, marginBottom: 24 }}>
        Ranking completo de proveedores y productos del mes, disponible en tu plan Gold o Enterprise.
      </div>
      <a
        href="https://wa.me/593968652005"
        target="_blank" rel="noopener noreferrer"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: '#25D366', color: '#fff',
          padding: '12px 28px', borderRadius: 99,
          fontSize: 14, fontWeight: 700, textDecoration: 'none',
          boxShadow: '0 4px 20px rgba(37,211,102,.3)',
        }}
      >
        Hablar con ventas
      </a>
    </div>
  </div>
);

/* ── Ranking Card skeleton ──────────────────────────────── */
const RankSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    {[1, 2, 3, 4].map(i => <Sk key={i} h={44} r={8} />)}
  </div>
);

/* ── Main component ─────────────────────────────────────── */
const AnalyticsTab = () => {
  const { restaurant, subscription, token } = useSelector((s) => s.auth);

  // Chart.js (existing) confirms API expects 'MM' format (e.g. "04")
  const month = moment().format('MM');

  const isGold = useMemo(() => {
    const abrev = subscription?.plan?.abreviacion?.toLowerCase() ?? '';
    const name = subscription?.plan?.name?.toLowerCase() ?? '';
    return abrev.includes('gold') || abrev.includes('enterprise') ||
           name.includes('gold') || name.includes('enterprise');
  }, [subscription]);

  const [base, setBase] = useState({ loading: true, orders: null, total: 0, count: 0, chart: [] });
  const [gold, setGold] = useState({ loading: true, topS: null, topP: null });

  useEffect(() => {
    if (!restaurant || !token) return;
    loadBase();
    loadGold();
  }, [restaurant, token]);

  /* ── Carga libre (todos los planes) ────────────────────── */
  const loadBase = async () => {
    setBase(p => ({ ...p, loading: true }));
    const [ordersRes, supplierRes] = await Promise.allSettled([
      gatTotalOrdersBymonth(restaurant, month, token),
      getSpendSupplier(restaurant, month, token),
    ]);

    // Parse orders — API may return a plain number or an object
    const ordersRaw = ordersRes.status === 'fulfilled' ? ordersRes.value : null;
    let orders = null;
    if (ordersRaw != null) {
      if (typeof ordersRaw === 'object') {
        orders = toNum(ordersRaw.count ?? ordersRaw.total ?? ordersRaw.orders ?? Object.values(ordersRaw)[0]);
      } else {
        orders = toNum(ordersRaw);
      }
    }

    // Parse supplier spend — handle all reasonable response shapes
    const sup = supplierRes.status === 'fulfilled' ? supplierRes.value : null;
    let info = [];
    if (sup) {
      if (Array.isArray(sup))                    info = sup;
      else if (Array.isArray(sup.information))   info = sup.information;
      else if (Array.isArray(sup.data))          info = sup.data;
      else if (Array.isArray(sup.suppliers))     info = sup.suppliers;
      else if (Array.isArray(sup.items))         info = sup.items;
      else if (Array.isArray(sup.results))       info = sup.results;
    }

    const chart = info
      .map(s => ({
        name: s.name || s.supplier_name || s.supplierName || 'Sin nombre',
        value: toNum(s.totalprov ?? s.total ?? s.amount ?? s.spend ?? s.totalSpend ?? s.totalAmount),
      }))
      .filter(s => s.value > 0)
      .sort((a, b) => b.value - a.value);

    // Use root total if present, otherwise sum from chart items
    const rootTotal = toNum(sup?.total ?? sup?.totalAmount ?? sup?.grand_total ?? sup?.totalSpend);
    const calcTotal = chart.reduce((acc, s) => acc + s.value, 0);
    const total = rootTotal > 0 ? rootTotal : calcTotal;

    setBase({ loading: false, orders, total, count: chart.length, chart });
  };

  /* ── Carga Gold ─────────────────────────────────────────── */
  const loadGold = async () => {
    setGold({ loading: true, topS: null, topP: null });
    const [sRes, pRes] = await Promise.allSettled([
      getCurrentMonthTopSuppliers(restaurant, token, 8),
      getCurrentMonthTopProducts(restaurant, token, 8),
    ]);
    setGold({
      loading: false,
      topS: sRes.status === 'fulfilled' ? sRes.value : null,
      topP: pRes.status === 'fulfilled' ? pRes.value : null,
    });
  };

  /* ── Derived data ───────────────────────────────────────── */
  const suppMain  = (gold.topS?.suppliers ?? []).filter(s => s.name !== 'Otros');
  const suppOther = (gold.topS?.suppliers ?? []).find(s => s.name === 'Otros');
  const suppMax   = Math.max(...suppMain.map(s => toNum(s.total)), 1);
  const suppChart = suppMain.map(s => ({ name: s.name, value: toNum(s.total) }));

  const prodMain  = (gold.topP?.products ?? []).filter(p => p.name !== 'Otros');
  const prodOther = (gold.topP?.products ?? []).find(p => p.name === 'Otros');
  const prodMax   = Math.max(...prodMain.map(p => toNum(p.total)), 1);
  const prodChart = prodMain.map(p => ({ name: p.name, value: toNum(p.total) }));

  const goldTotal = toNum(gold.topS?.total) || toNum(gold.topP?.total);

  // Fallback: si getSpendSupplier devuelve vacío, usar datos de gold
  const displayTotal = base.total > 0 ? base.total : goldTotal;
  const displayCount = base.count > 0 ? base.count : suppMain.length;
  const kpiLoading   = base.loading || (base.total === 0 && base.count === 0 && gold.loading);

  return (
    <div className={styles.restrictedGate}>
      <div className={`${styles.restrictedContent} ${!isGold ? styles.blurred : ''}`} style={{ paddingBottom: 56 }}>
      <style>{`
        @keyframes at-sk {
          0%   { background-position: 300% 0; }
          100% { background-position: -300% 0; }
        }
      `}</style>

      {/* ── KPI Cards ─────────────────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 14,
        marginBottom: 36,
      }}>
        <KpiCard
          label="Total gastado"
          value={!kpiLoading ? fmtUSD(displayTotal) : null}
          sub={nowMonthStr()}
          accent="#2D6A4F" icon="dollar" loading={kpiLoading}
        />
        <KpiCard
          label="Órdenes"
          value={!base.loading ? String(base.orders ?? 0) : null}
          sub="realizadas este mes"
          accent="#49A35B" icon="bag" loading={base.loading}
        />
        <KpiCard
          label="Proveedores activos"
          value={!kpiLoading ? String(displayCount) : null}
          sub="con compras este mes"
          accent="#40916C" icon="truck" loading={kpiLoading}
        />
      </div>

      {/* ── Rankings del mes (Gold) ───────────────────────── */}
      <SectionHeader
        icon="trophy"
        title="Rankings del mes"
        sub={goldTotal > 0 ? `Total del mes: ${fmtUSD(goldTotal)} · ${nowMonthStr()}` : undefined}
        style={{ marginBottom: 20 }}
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 18,
      }}>
        <RankingCard
          title="Top proveedores"
          sub="Por gasto en el mes"
          iconName="truck"
          chartData={suppChart}
          rankItems={suppMain}
          rankMax={suppMax}
          rankOther={suppOther}
          otherLabel="Otros proveedores"
          loading={gold.loading}
          renderSub={(s) => null}
        />
        <RankingCard
          title="Top productos"
          sub="Por producto comprado"
          iconName="package"
          chartData={prodChart}
          rankItems={prodMain}
          rankMax={prodMax}
          rankOther={prodOther}
          otherLabel="Otros productos"
          loading={gold.loading}
          renderSub={(p) => [
            p.supplierName,
            p.orderCount != null && `${p.orderCount} ped.`,
            p.totalQuantity != null && `${p.totalQuantity} uds.`,
          ].filter(Boolean).join(' · ') || null}
        />
      </div>
    </div>
    {!isGold && <ClientRestrictedFeatureOverlay />}
  </div>
  );
};

/* ── Ranking Card con tab Ranking / Gráfica ─────────────── */
const RankingCard = ({
  title, sub, iconName, chartData, rankItems, rankMax,
  rankOther, otherLabel, loading, renderSub,
}) => {
  const [view, setView] = React.useState('ranking');

  const TabBtn = ({ id, label }) => (
    <button
      onClick={() => setView(id)}
      style={{
        padding: '5px 14px', borderRadius: 99, border: 'none',
        background: view === id ? '#2D6A4F' : 'transparent',
        color: view === id ? '#fff' : '#888',
        fontSize: 12, fontWeight: 600, cursor: 'pointer',
        transition: 'all .15s',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{
      background: '#fff', borderRadius: 18,
      padding: '22px 24px 26px',
      boxShadow: '0 2px 20px rgba(0,0,0,.06)',
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10, background: '#f0faf4',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name={iconName} size={16} color="#2D6A4F" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#111' }}>{title}</div>
            <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>{sub}</div>
          </div>
        </div>

        {/* Tab toggle */}
        <div style={{
          display: 'flex', background: '#f3f3f3',
          borderRadius: 99, padding: 3, gap: 2,
        }}>
          <TabBtn id="ranking" label="Ranking" />
          <TabBtn id="grafica" label="Gráfica" />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <RankSkeleton />
      ) : view === 'grafica' ? (
        <HBar data={chartData} loading={false} />
      ) : rankItems.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {rankItems.map((item, i) => (
            <RankRow
              key={i} rank={i + 1}
              name={item.name} amount={item.total}
              sub={renderSub(item) || undefined}
              max={rankMax} color={G[i % G.length]}
            />
          ))}
          {rankOther && (
            <div style={{
              padding: '9px 14px', background: '#f8f7f4', borderRadius: 10,
              display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#777',
            }}>
              <span>{otherLabel}</span>
              <span style={{ fontWeight: 700 }}>{fmtUSD(rankOther.total)}</span>
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '28px 0', fontSize: 13 }}>
          Sin datos este mes
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
